import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Blocks, Database, Globe, MessageSquare } from "lucide-react";
import { cn } from "@/lib/cn";
import { CrmIntegrationList } from "./components/CrmIntegrationList";
import { CrmIntegrationDetail } from "./components/CrmIntegrationDetail";
import { PropcoIntegrationDetail } from "./components/PropcoIntegrationDetail";
import { ReapitIntegrationDetail } from "./components/ReapitIntegrationDetail";
import { CommunicationChannelTabs } from "./components/CommunicationChannelTabs";

type Tab = "crms" | "portals" | "channels";

export const IntegrationsPage: React.FC = () => {
  const { crm } = useParams<{ crm: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("crms");

  const tabs = [
    { id: "crms", label: "CRMs", icon: Database },
    { id: "portals", label: "Portals", icon: Globe },
    { id: "channels", label: "Communication Channels", icon: MessageSquare },
  ];

  if (crm) {
    // Propco has no hierarchy to map — it is a single group-wide account, so it
    // gets its own credentials screen instead of the ref-mapping one.
    if (crm.toUpperCase() === "PROPCO") {
      return <PropcoIntegrationDetail onBack={() => navigate("/integrations")} />;
    }
    if (crm.toUpperCase() === "REAPIT") {
      return <ReapitIntegrationDetail onBack={() => navigate("/integrations")} />;
    }
    return <CrmIntegrationDetail crmId={crm} onBack={() => navigate("/integrations")} />;
  }

  return (
    <div className="flex flex-col h-full bg-bridge-bg">
      <header className="px-6 py-4 bg-white border-b border-bridge-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-1.5 bg-bridge-accent/10 rounded-lg text-bridge-accent">
            <Blocks className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-bridge-fg tracking-tight">Integrations</h1>
            <p className="text-xs text-bridge-muted">Connect Bridge AI with your existing tools and platforms.</p>
          </div>
        </div>

        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={cn(
                "flex items-center gap-2 py-2 text-xs font-semibold transition-all relative",
                activeTab === tab.id
                  ? "text-bridge-accent"
                  : "text-bridge-muted hover:text-bridge-fg"
              )}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-bridge-accent rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </header>

      <main className="flex-1 p-6 overflow-auto">
        {activeTab === "crms" && <CrmIntegrationList />}
        {activeTab === "portals" && (
          <div className="flex flex-col items-center justify-center h-full text-center py-20">
            <div className="p-3 bg-bridge-bg rounded-full mb-3">
              <Globe className="w-10 h-10 text-bridge-muted/30" />
            </div>
            <h3 className="text-base font-semibold text-bridge-fg">No Portals Integrated</h3>
            <p className="text-xs text-bridge-muted max-w-sm mt-2">
              Connect your property portals to unify lead capture and tracking across all sources.
            </p>
          </div>
        )}
        {activeTab === "channels" && <CommunicationChannelTabs />}
      </main>
    </div>
  );
};
